import React, { useState, useEffect } from "react";
import { Table, Button, Tag, Tooltip, Modal, Space, message } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import ECATableFilters from "./ECATableFilters.jsx";
import CreateECAModal from "./CreateECAModal";
import EditECAModal from "./EditECAModal";
import ViewParticipantsModal from "./ViewParticipantsModal";
import { getAllEcas, deleteEca, getAllEcasParticipantes } from '../../services/EcasParticipante/ecaParticipanteService.js';
import { selectAppPermissions } from '../../store/auth/selectors';
import { useSelector } from 'react-redux';
import ExportECAButton from "./ExportECAButton.jsx";
export default function ECATable({ proyectoCodigo }) {
  const [ecas, setEcas] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [ecaSeleccionada, setEcaSeleccionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ busqueda: "", tipoEstado: [], sesiones: [], proyecto: null, });
  const [currentPage, setCurrentPage] = useState(1);
  const [ecasParticipantes, setEcasParticipantes] = useState([]);

  const pageSize = 10;

  const appName = 'ecas_participante';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionDelete = permissions.priv_delete === 'Y';
  const permissionUpdate = permissions.priv_update === 'Y';
  const permissionInsert = permissions.priv_insert === 'Y';
  const permissionPrint = permissions.priv_print === 'Y';
  const permissionExport = permissions.priv_export === 'Y';

  const user = useSelector((state) => state.auth.user);
  const userLogin = user.username;


useEffect(() => {
  if (proyectoCodigo && ecas.length > 0) {
    const proyectoMatch = ecas.find(
      (e) => e.codigo_proyecto?.trim() === proyectoCodigo?.trim()
    );

    if (proyectoMatch) {
      setFiltros((prev) => ({
        ...prev,
        proyecto: String(proyectoMatch.id_proyecto),
      }));
    }
  }
}, [proyectoCodigo, ecas]);

  const cargarECAsParticipantes = async () => {
    try {
      const data = await getAllEcasParticipantes();
      if (!Array.isArray(data)) {
        throw new Error("Formato de respuesta inválido: se esperaba un array.");
      }
      setEcasParticipantes(data);
    } catch (error) {
      console.error("Error al cargar participantes:", error);
      message.error("No se pudieron cargar los participantes");
    }
  };

  const cargarECAs = async () => {
    try {
      setLoading(true);
      const data = await getAllEcas();
      if (!Array.isArray(data)) {
        throw new Error("Formato de respuesta inválido: se esperaba un array.");
      }
      const adaptadas = data.map((eca) => ({
        id: eca.oid,
        oid: eca.oid,
        nombre: eca.nombreECA,
        participantes: eca.participantes,
        fechaCreacion: eca.fechaCreacion,
        estado: eca.estado,
        estadoTexto: eca.estadoTexto,
        observacion: eca.observacion,
        departamento: eca.departamento,
        municipio: eca.municipio,
        nombre_departamento: eca.nombre_departamento,
        nombre_municipio: eca.nombre_municipio,
        tipo_estado: eca.tipo_estado,
        conformaciones: eca.conformaciones,
        tipoestadoname: eca.tipoestadoname,
        proyecto: eca.proyecto,
        id_proyecto: eca.id_proyecto,
        codigo_proyecto: eca.codigo_proyecto,

      }));
      setEcas(adaptadas);
    } catch (error) {
      console.error("Error al cargar ECAs:", error);
      message.error("No se pudieron cargar las ECAs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarECAs();
    cargarECAsParticipantes();
  }, []);

  const handleEliminar = (id) => {
    if (!permissionDelete) {
      return message.warning("No tienes permiso para eliminar una ECA");
    }

    Modal.confirm({
      title: "¿Estás seguro?",
      content: "Esta acción eliminará permanentemente la ECA y sus datos asociados.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await deleteEca(id, userLogin);
          setEcas(prev => prev.filter(eca => eca.oid !== id));
          message.success("ECA eliminada correctamente");
        } catch (error) {
          console.error("Error al eliminar ECA:", error);
          message.error("No se pudo eliminar la ECA");
        }
      },
    });
  };

  const handleEditar = (eca) => {
    setEcaSeleccionada(eca);
    setOpenEditModal(true);
  };

  const handleVerParticipantes = (eca) => {
    setEcaSeleccionada(eca);
    setOpenViewModal(true);
  };

  const handleECACreada = (nueva) => {
    setEcas((prev) => [...prev, { ...nueva, id: nueva.oid }]);
    setOpenCreateModal(false);
  };

  const handleECAUpdated = (actualizada) => {
    setEcas(ecas.map((eca) => (eca.id === actualizada.id ? actualizada : eca)));
    setOpenEditModal(false);
  };



  const ecasFiltradas = ecas.filter((eca) => {
  const nombre = eca.nombre ?? "";

  const matchNombre = nombre
    .toLowerCase()
    .includes(filtros.busqueda?.toLowerCase() ?? "");

  const ecaTipos = Array.isArray(eca.tipo_estado)
    ? eca.tipo_estado.map((v) => String(v).trim())
    : typeof eca.tipo_estado === "string"
    ? eca.tipo_estado.split(",").map((v) => v.trim())
    : [];

  const matchTipoEstado =
    filtros.tipoEstado.length === 0 ||
    filtros.tipoEstado.every((filtro) =>
      ecaTipos.includes(String(filtro))
    );

  const matchSesion =
    filtros.sesiones.length === 0 ||
    filtros.sesiones.includes(eca.nombre);

  const matchProyectoInterno =
  !filtros.proyecto ||
  String(eca.id_proyecto) === String(filtros.proyecto);

const matchProyectoExterno =
  !proyectoCodigo ||
  eca.codigo_proyecto?.trim() === proyectoCodigo?.trim();

  return (
    matchNombre &&
    matchTipoEstado &&
    matchSesion &&
    matchProyectoInterno &&
    matchProyectoExterno
  );
});

  const participantesFiltrados = ecasParticipantes.filter((participante) => {
    const oidEcaParticipante = participante.oid;
    const existeEnFiltradas = ecasFiltradas.some((eca) => {
      return String(eca.oid) === String(oidEcaParticipante);
    });
    return oidEcaParticipante && existeEnFiltradas;
  });

  const columnas = [
    {
      title: '#',
      key: 'index',
      render: (_text, _record, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Sesiones de ECA's",
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: "Departamento",
      dataIndex: 'nombre_departamento',
      key: 'nombre_departamento',
    },
    {
      title: "Municipio",
      dataIndex: 'nombre_municipio',
      key: 'nombre_municipio',
    },
    {
      title: "Proyecto",
      dataIndex: 'proyecto',
      key: 'proyecto',
    },
    {
      title: "Conformaciones",
      dataIndex: "conformaciones",
      key: "conformaciones",
      align: "center",
    },
    {
      title: "Fecha sesión ECA",
      dataIndex: "fechaCreacion",
      align: "center",
      render: (val) => new Date(val).toLocaleDateString("es-ES"),
    },
    {
      title: "Tipo de Temática",
      width: 310,
      dataIndex: "tipo_estado",
      render: (tipo_estado) => {
        const estadoMap = {
          1: { label: 'Técnico', color: 'blue' },
          2: { label: 'Ambiental', color: 'green' },
          3: { label: 'Socioempresarial', color: 'lime' },
          4: { label: 'Comercial', color: 'purple' },
          5: { label: 'DHAA', color: 'gold' },
          6: { label: 'Enfoque Diferencial', color: 'cyan' }
        };

        if (!tipo_estado) return <Tag color="gray">Sin definir</Tag>;

        // convertir a array si viene como string
        const ids = Array.isArray(tipo_estado)
          ? tipo_estado.map((v) => parseInt(v))
          : typeof tipo_estado === "string"
            ? tipo_estado.split(",").map((v) => parseInt(v.trim()))
            : [];

        return (
          <Space wrap>
            {ids.map((id) => {
              const info = estadoMap[id] || { label: 'Desconocido', color: 'gray' };
              return <Tag key={id} color={info.color}>{info.label}</Tag>;
            })}
          </Space>
        );
      }
    }, {
      title: "Participantes",
      dataIndex: "participantes",
      align: "center",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    // Condicionalmente agregar la columna de acciones
    ...(permissionPrint || permissionUpdate || permissionDelete
      ? [
        {
          title: "Acciones",
          key: "acciones",
          align: "center",
          render: (_, record) => (
            <Space>
              {permissionPrint && (
                <Tooltip title="Ver participantes">
                  <Button icon={<EyeOutlined />} onClick={() => handleVerParticipantes(record)} />
                </Tooltip>
              )}
              {permissionUpdate && (
                <Tooltip title="Editar">
                  <Button icon={<EditOutlined />} onClick={() => handleEditar(record)} />
                </Tooltip>
              )}
              {permissionDelete && (
                <Tooltip title="Eliminar">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleEliminar(record.id)}
                  />
                </Tooltip>
              )}
            </Space>
          ),
        },
      ]
      : []),
  ];

  const listaProyectos = Array.from(
    new Map(
      ecas
        .filter((e) => e.id_proyecto != null)
        .map((e) => [
          String(e.id_proyecto),
          {
            id_proyecto: e.id_proyecto,
            proyecto: e.proyecto,           // "P25 Ovino Caprino, Zambrano"
            codigo_proyecto: e.codigo_proyecto, // "P25"
          },
        ])
    ).values()
  );

  return (
    <div className="container">
      <div className="row mb-2 d-flex align-items-center">
        <div className="col-md-7 linea_separador mb-2 d-flex align-items-center">
          <div className="titulo_proyecto" style={{ flexBasis: '25%', flexGrow: 0 }}>
            <p>PROYECTO UTF <br /> COL 160 COL</p>
          </div>
          <div className="objeto" style={{ flexBasis: '75%', flexGrow: 0 }}>
            <p>
              Implementar actividades de desarrollo económico, derecho humano a la alimentación y fortalecimiento de capacidades
              institucionales, comunitarias y organizacionales que aporten al cierre de brechas y la transformación de los territorios PDET
            </p>
          </div>
        </div>
        <div className="col-md-5 d-flex flex-column align-items-center justify-content-center gap-3 text-center">
          <h4 className="text-center mb-4">
            <UsergroupAddOutlined style={{ fontSize: 24, marginRight: 8 }} />
            Sesiones de ECA's
          </h4>

          <div className="d-flex flex-wrap justify-content-center gap-2">
            {permissionInsert && (
              <Button
                type="primary"
                size="middle"
                onClick={() => setOpenCreateModal(true)}
                style={{ minWidth: 200 }}
              >
                Registrar sesión de ECA's
              </Button>
            )}
            {permissionExport && (
              <ExportECAButton
                data={ecasFiltradas}
                participantes={participantesFiltrados}
              />
            )}
          </div>
        </div>

      </div>
      <ECATableFilters
        proyectoCodigo={proyectoCodigo}
        filtros={{ ...filtros, listaSesiones: ecas.map((e) => e.nombre), listaProyectos }}
        onFiltrosChange={setFiltros}
      />
      <>
        <div className="participant-count-container" style={{ marginTop: 10, marginBottom: 10 }}>
          <h1 className="participant-count-title">
            <span className="participant-count-number">
              {ecasFiltradas.length > 0 && (
                <div style={{ marginTop: 12, }}>
                  Total Sesión ECA's: {ecasFiltradas.length}
                  {ecasFiltradas.reduce((acc, eca) => acc + eca.participantes, 0)}
                </div>
              )}
            </span>
          </h1>
        </div>
        <Table
          columns={columnas}
          dataSource={ecasFiltradas}
          rowKey="oid"
          loading={loading}
          pagination={{
            pageSize,
            onChange: (page) => setCurrentPage(page),
          }}
          locale={{ emptyText: "No hay Sesiones de ECA's registradas" }}
        />
        <CreateECAModal
          isOpen={openCreateModal}
          onClose={() => setOpenCreateModal(false)}
          onECACreated={handleECACreada}
          user={userLogin}
        />
        {ecaSeleccionada && (
          <EditECAModal
            isOpen={openEditModal}
            onClose={() => {
              setOpenEditModal(false)
              setEcaSeleccionada(null)
              cargarECAs();
            }}
            eca={ecaSeleccionada}
            onECAUpdated={handleECAUpdated}
            user={userLogin}
          />
        )}
        {ecaSeleccionada && (
          <ViewParticipantsModal
            isOpen={openViewModal}
            onClose={() => {
              setOpenViewModal(false)
              setEcaSeleccionada(null)
            }}
            eca={ecaSeleccionada}
          />
        )}
      </>
    </div>
  );
}
